import { useCallback, useEffect, useRef, type Dispatch, type SetStateAction } from "react";
import { useSearchParams, type SetURLSearchParams } from "react-router-dom";

interface UsePageStateOptions {
  namespace?: string;
}

function parsePositiveInt(raw: string | null, fallback: number): number {
  if (raw === null) return fallback;
  const parsed = Number(raw);
  return Number.isInteger(parsed) && parsed >= 1 ? parsed : fallback;
}

export function usePageState(initialSize = 10, options?: UsePageStateOptions): {
  page: number;
  pageSize: number;
  totalPages: (total: number) => number;
  setPage: Dispatch<SetStateAction<number>>;
  handlePageSizeChange: (size: number) => void;
} {
  const [searchParams, setSearchParams] = useSearchParams();
  // react-router recreates `setSearchParams` every time the URL changes (it's
  // memoized on `searchParams`, which changes on every page navigation). A ref
  // lets setPage/handlePageSizeChange call the latest setter without taking on
  // that instability themselves — see stability note below.
  const setSearchParamsRef = useRef<SetURLSearchParams>(setSearchParams);
  useEffect(() => {
    setSearchParamsRef.current = setSearchParams;
  });

  const pageKey = options?.namespace ? `${options.namespace}_page` : "page";
  const pageSizeKey = options?.namespace ? `${options.namespace}_pageSize` : "pageSize";

  const page = parsePositiveInt(searchParams.get(pageKey), 1);
  const pageSize = parsePositiveInt(searchParams.get(pageSizeKey), initialSize);

  const totalPages = useCallback((total: number) => Math.ceil(total / pageSize), [pageSize]);

  // Stable for the component's lifetime (deps only on pageKey, a constant
  // string derived from the caller's namespace — never setSearchParams,
  // whose identity changes on every page navigation). Consumers rely on that
  // permanent stability in effect dependency arrays (e.g. MembersPage resets
  // to page 1 on search change via `useEffect(..., [debounced, setPage])`):
  // if setPage's identity changed on every page navigation too, that effect
  // would re-fire on every click and immediately bounce the page back to 1.
  const setPage = useCallback<Dispatch<SetStateAction<number>>>(
    (value) => {
      setSearchParamsRef.current(
        (prev) => {
          const currentPage = parsePositiveInt(prev.get(pageKey), 1);
          const next = typeof value === "function" ? (value as (p: number) => number)(currentPage) : value;
          const params = new URLSearchParams(prev);
          params.set(pageKey, String(next));
          return params;
        },
        { replace: true },
      );
    },
    [pageKey],
  );

  const handlePageSizeChange = useCallback(
    (size: number) => {
      setSearchParamsRef.current(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.set(pageSizeKey, String(size));
          params.set(pageKey, "1");
          return params;
        },
        { replace: true },
      );
    },
    [pageKey, pageSizeKey],
  );

  return { page, pageSize, totalPages, setPage, handlePageSizeChange };
}
